import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { BaseProduct } from '@/api/suppliers'
import { catalogApi, suppliersApi } from '@/api/suppliers'
import { Alert, Button } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'

/**
 * Επιλογή ειδών από τον βασικό κατάλογο, ανά κατηγορία (§5.2).
 *
 * Ο χρήστης τσεκάρει όσα θέλει και τα προσθέτει με μία κίνηση, αντί να
 * πληκτρολογεί ένα ένα. Τα ήδη προστεθειμένα εμφανίζονται απενεργοποιημένα,
 * ώστε να μη χρειάζεται να θυμάται τι έχει ήδη βάλει.
 */
export function CatalogPicker({
  supplierId,
  existingBaseProductIds,
  onDone,
}: {
  supplierId: string
  existingBaseProductIds: Set<string>
  onDone: () => void
}) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const [category, setCategory] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const categories = useQuery({ queryKey: ['base-categories'], queryFn: catalogApi.categories })
  const products = useQuery({ queryKey: ['base-products'], queryFn: () => catalogApi.baseProducts() })

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('el')
    return (products.data ?? []).filter(
      (product) =>
        (!category || product.category === category) &&
        (!term || product.name.toLocaleLowerCase('el').includes(term)),
    )
  }, [products.data, category, search])

  const add = useMutation({
    mutationFn: () => {
      const chosen = (products.data ?? []).filter((p) => selected.has(p.id))
      return suppliersApi.createItemsBulk(
        supplierId,
        chosen.map((product) => ({
          baseProductId: product.id,
          unit: product.defaultUnit,
        })),
      )
    },
    onSuccess: () => {
      setSelected(new Set())
      onDone()
    },
  })

  const toggle = (product: BaseProduct) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(product.id)) next.delete(product.id)
      else next.add(product.id)
      return next
    })
  }

  return (
    <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
      <h2 className="mb-1 font-semibold text-gray-900">{t('catalog.title')}</h2>
      <p className="mb-3 text-sm text-gray-500">{t('catalog.hint')}</p>

      {add.isError && (
        <div className="mb-3">
          <Alert>{translateError(add.error)}</Alert>
        </div>
      )}

      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('catalog.searchPlaceholder')}
        className="mb-3 h-touch w-full rounded-xl border border-gray-300 bg-white px-4 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
      />

      {/* Οι κατηγορίες κυλούν οριζόντια: σε στενή οθόνη δεν χωρούν σε γραμμές. */}
      <div className="-mx-4 mb-3 overflow-x-auto px-4">
        <div className="flex w-max gap-2">
          <CategoryChip
            label={t('catalog.all')}
            active={category === null}
            onClick={() => setCategory(null)}
          />
          {categories.data?.map((value) => (
            <CategoryChip
              key={value}
              label={value}
              active={category === value}
              onClick={() => setCategory(value)}
            />
          ))}
        </div>
      </div>

      {products.isPending && <p className="text-gray-500">{t('common.loading')}</p>}

      {visible.length === 0 && !products.isPending && (
        <p className="py-6 text-center text-sm text-gray-500">{t('catalog.noResults')}</p>
      )}

      <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
        {visible.map((product) => {
          const already = existingBaseProductIds.has(product.id)
          const checked = selected.has(product.id)

          return (
            <li key={product.id}>
              <label
                className={
                  'flex cursor-pointer items-center gap-3 py-3 ' +
                  (already ? 'cursor-not-allowed opacity-40' : '')
                }
              >
                <input
                  type="checkbox"
                  disabled={already}
                  checked={checked || already}
                  onChange={() => toggle(product)}
                  className="size-5 shrink-0 rounded border-gray-300 accent-brand-700"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-gray-900">{product.name}</span>
                  <span className="block text-xs text-gray-400">
                    {product.category} · {t(`units.${product.defaultUnit}`)}
                  </span>
                </span>
                {already && (
                  <span className="shrink-0 text-xs text-gray-500">{t('catalog.already')}</span>
                )}
              </label>
            </li>
          )
        })}
      </ul>

      <div className="mt-4 flex flex-col gap-2">
        <Button
          loading={add.isPending}
          disabled={selected.size === 0}
          onClick={() => add.mutate()}
        >
          {t('catalog.addSelected', { count: selected.size })}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </section>
  )
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={
        'whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ' +
        (active
          ? 'border-brand-700 bg-brand-700 text-white'
          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400')
      }
    >
      {label}
    </button>
  )
}
